import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { DECISIONS, formatDateTime } from "../../lib/cefr";

export function DecisionPanel({ attemptId, initial, onSaved }) {
  const [status, setStatus] = useState(initial?.status || "pending");
  const [comment, setComment] = useState(initial?.comment || "");
  const [meta, setMeta] = useState(initial);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/attempts/${attemptId}/decision`, { status, comment });
      setMeta(data);
      onSaved?.(data);
      toast.success("Decision saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save decision");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8 neu p-6" data-testid="decision-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-xl font-bold">Admission decision</h2>
        <span className="text-xs text-muted-foreground">Visible to admins only</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2" role="radiogroup">
        {Object.entries(DECISIONS).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={status === id}
            onClick={() => setStatus(id)}
            data-testid={`decision-option-${id}`}
            className={`px-4 py-2 text-sm font-bold border transition-colors ${
              status === id
                ? id === "accepted" ? "bg-emerald-600 text-white border-emerald-600"
                  : id === "rejected" ? "bg-red-600 text-white border-red-600"
                  : "bg-foreground text-background border-foreground"
                : "neu-btn"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={2000}
        rows={4}
        placeholder="Add a comment for your colleagues (optional)"
        data-testid="decision-comment-input"
        className="mt-4 w-full border border-foreground/30 bg-transparent p-3 text-sm focus:outline-none focus:border-foreground"
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground" data-testid="decision-meta">
          {meta?.updated_at ? `Last saved ${formatDateTime(meta.updated_at)}${meta.updated_by ? ` by ${meta.updated_by}` : ""}` : "Not decided yet"}
        </span>
        <button
          onClick={save}
          disabled={saving}
          data-testid="decision-save-button"
          className="px-5 py-2 text-sm font-bold bg-foreground text-background border border-foreground hover:bg-signal hover:border-signal hover:text-ink disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save decision"}
        </button>
      </div>
    </section>
  );
}

export function DecisionTag({ decision }) {
  const s = decision?.status || "pending";
  const tone = s === "accepted" ? "text-emerald-600 border-emerald-600" : s === "rejected" ? "text-red-600 border-red-600" : "text-muted-foreground border-foreground/30";
  return (
    <span className={`inline-flex text-xs font-bold px-2 py-0.5 border ${tone}`} title={decision?.comment || ""} data-decision={s}>
      {DECISIONS[s]}
    </span>
  );
}
